import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Search, Brain, Database, Sparkles, BarChart3, Check, Loader2 } from 'lucide-react';
import { REASONING_STEPS } from './demoMockData';

interface AIReasoningAnimationProps {
  isActive: boolean;
  onComplete: () => void;
}

const iconMap = {
  search: Search,
  brain: Brain,
  database: Database,
  sparkles: Sparkles,
  chart: BarChart3,
};

/**
 * AI reasoning animation with multi-step progress.
 * Light theme with clean card styling.
 */
export function AIReasoningAnimation({
  isActive,
  onComplete,
}: AIReasoningAnimationProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isActive) {
      setCurrentStepIndex(-1);
      setCompletedSteps(new Set());
      return;
    }

    let stepIndex = 0;

    const processStep = () => {
      if (stepIndex >= REASONING_STEPS.length) {
        setTimeout(onComplete, 600);
        return;
      }

      const step = REASONING_STEPS[stepIndex];
      setCurrentStepIndex(stepIndex);

      setTimeout(() => {
        setCompletedSteps(prev => new Set(prev).add(step.id));
        stepIndex++;
        processStep();
      }, step.duration);
    };

    setTimeout(processStep, 400);
  }, [isActive, onComplete]);

  if (!isActive) return null;

  return (
    <div className="relative">
      {/* Main content */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
      >
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-10 h-10 bg-[#063F6B] rounded-xl flex items-center justify-center">
            <Brain className="w-5 h-5 text-[#EAA059]" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#063F6B]">
              Analysis in Progress
            </h3>
            <p className="text-sm text-gray-500">
              Evaluating vendor options for your requirements
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-2">
          {REASONING_STEPS.map((step, index) => {
            const Icon = iconMap[step.icon];
            const isCompleted = completedSteps.has(step.id);
            const isCurrent = index === currentStepIndex && !isCompleted;
            const isPending = index > currentStepIndex;

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`
                  flex items-center gap-3 p-3 rounded-lg transition-all duration-300
                  ${isCurrent ? 'bg-[#EAA059]/10 border border-[#EAA059]/30' : ''}
                  ${isCompleted ? 'bg-emerald-50' : ''}
                  ${isPending ? 'opacity-40' : ''}
                `}
              >
                {/* Status icon */}
                <div className={`
                  flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300
                  ${isCompleted ? 'bg-emerald-100' : isCurrent ? 'bg-[#EAA059]/20' : 'bg-gray-100'}
                `}>
                  {isCompleted ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <Check className="w-4 h-4 text-emerald-600" />
                    </motion.div>
                  ) : isCurrent ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <Loader2 className="w-4 h-4 text-[#EAA059]" />
                    </motion.div>
                  ) : (
                    <Icon className="w-4 h-4 text-gray-400" />
                  )}
                </div>

                {/* Step text */}
                <span className={`
                  text-sm transition-colors duration-300
                  ${isCompleted ? 'text-emerald-700' : isCurrent ? 'text-[#063F6B]' : 'text-gray-400'}
                `}>
                  {step.text}
                </span>

                {/* MCP Apps badge for the visualization step */}
                {step.id === 'visualize' && isCurrent && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="ml-auto px-2 py-1 bg-[#EAA059]/15 rounded-full text-xs text-[#EAA059] font-medium flex items-center gap-1"
                  >
                    <Sparkles className="w-3 h-3" />
                    MCP Apps
                  </motion.span>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#EAA059] rounded-full"
              initial={{ width: '0%' }}
              animate={{
                width: `${((completedSteps.size) / REASONING_STEPS.length) * 100}%`,
              }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-400">
            <span>Analyzing {847} vendors</span>
            <span>{completedSteps.size} of {REASONING_STEPS.length} steps</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Inline thinking indicator for the chat bubble.
 */
export function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      >
        <Loader2 className="w-4 h-4 text-[#EAA059]" />
      </motion.div>
      <span>Analyzing your requirements...</span>
    </div>
  );
}
